import React, { useState } from 'react';
import { X, Check, Crown, Zap, ChevronDown } from 'lucide-react';
import { SUBSCRIPTION_TIERS } from '../../services/subscriptionService';

const PricingModal = ({ isOpen, onClose, onStartTrial, userSubscription, hasUsedTrial = false }) => {
  const [loading, setLoading] = useState(false);
  const [expandedPlan, setExpandedPlan] = useState(null);

  if (!isOpen) return null;

  const isTrialActive = userSubscription?.subscriptionStatus === 'trial' &&
    userSubscription?.trialEndDate &&
    new Date(userSubscription.trialEndDate) > new Date();
  const isPremium = userSubscription?.subscriptionStatus === 'active';
  const isExpired = userSubscription?.subscriptionStatus === 'expired';
  const isFreePlan = !userSubscription
    || userSubscription?.subscriptionStatus === 'free'
    || userSubscription?.subscriptionPlan === 'free'
    || userSubscription?.subscription?.tier === 'FREE';
  const couponDiscount = userSubscription?.couponDiscountPercent || 0;
  const basePrice = SUBSCRIPTION_TIERS.PREMIUM.price;
  const discountedPrice = couponDiscount ? (basePrice * (1 - couponDiscount / 100)).toFixed(2) : basePrice.toFixed(2);

  const shouldSubscribe = hasUsedTrial || isExpired;
  const checkBadgeStyle = { backgroundImage: 'var(--gradient-warn)' };

  const handleStartTrial = async () => {
    try {
      setLoading(true);
      await onStartTrial();
      onClose();
    } catch (error) {
      console.error('Error starting trial:', error);
      alert('Failed to start trial. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const freeFeatures = [
    'Add transactions manually - Just type in what you earned or spent',
    'Download your records anytime - Save as simple files you can share',
    'Set money goals & track progress - Know if you\'re hitting your targets',
    'Keep your data safe - Syncs with Google Drive'
  ];

  const premiumFeatures = [
    'All Free features',
    'Talk instead of typing - Add income or expenses just by speaking',
    'Snap & save receipts - Take a picture, and we\'ll fill in the details for you',
    'See how much you\'re really making - Automatic profit & tax calculations',
    'Know what to charge - Use our built-in pricing helper and overhead calculator',
    'See your money clearly - Simple charts that show where your money goes',
    'Track growth easily - Understand what\'s working in your business'
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-2xl font-bold text-[color:var(--color-text)]">Choose Your Plan</h2>
          <button
            onClick={onClose}
            aria-label="Close pricing"
            className="text-[color:var(--color-text)] hover:opacity-80 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Free Plan */}
            <div className="border-2 border-[color:var(--color-accent)] rounded-xl p-6 bg-accent-gradient">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-[color:var(--color-text)] mb-2">Free</h3>
                <div className="text-3xl font-bold text-[color:var(--color-text)] mb-1">€0</div>
                <div className="text-[color:var(--color-text)]">Forever</div>
              </div>

              <button
                onClick={() => setExpandedPlan(expandedPlan === 'free' ? null : 'free')}
                className="w-full py-2 px-4 mb-6 flex items-center justify-center gap-2 text-sm font-medium text-[color:var(--color-text)] hover:brightness-95 rounded transition"
              >
                View features
                <ChevronDown className={`w-4 h-4 transition-transform ${expandedPlan === 'free' ? 'rotate-180' : ''}`} />
              </button>

              {expandedPlan === 'free' && (
                <ul className="space-y-3 mb-6">
                  {freeFeatures.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0" style={checkBadgeStyle}>
                        <Check className="w-3.5 h-3.5 text-[color:var(--color-text)]" />
                      </div>
                      <div className="text-sm text-[color:var(--color-text)]">{feature}</div>
                    </li>
                  ))}
                </ul>
              )}

              <button
                disabled
                className="w-full py-3 px-4 bg-white text-[color:var(--color-text)] rounded-lg font-medium cursor-not-allowed"
              >
                {isFreePlan ? 'Current Plan' : 'Downgrade'}
              </button>
            </div>

            {/* Premium Plan */}
            <div className="border-2 border-[color:var(--color-warn)] rounded-xl p-6 bg-white relative">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <div className="bg-[color:var(--color-warn)] text-[color:var(--color-text)] px-4 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                  <Crown className="w-4 h-4" />
                  Most popular
                </div>
              </div>

              <div className="text-center mb-6 mt-4">
                <h3 className="text-xl font-bold text-[color:var(--color-text)] mb-2">Premium</h3>
                <div className="text-3xl font-bold text-[color:var(--color-text)] mb-1">
                  {couponDiscount ? (
                    <>
                      <span className="line-through text-gray-500 mr-2">€{basePrice.toFixed(2)}</span>
                      €{discountedPrice}
                    </>
                  ) : (
                    <>€{basePrice.toFixed(2)}</>
                  )}
                </div>
                <div className="text-[color:var(--color-text)]">per month {couponDiscount ? `(with ${couponDiscount}% off for life)` : ''}</div>
              </div>

              <button
                onClick={() => setExpandedPlan(expandedPlan === 'premium' ? null : 'premium')}
                className="w-full py-2 px-4 mb-6 flex items-center justify-center gap-2 text-sm font-medium text-[color:var(--color-text)] hover:brightness-95 rounded transition"
              >
                View features
                <ChevronDown className={`w-4 h-4 transition-transform ${expandedPlan === 'premium' ? 'rotate-180' : ''}`} />
              </button>

              {expandedPlan === 'premium' && (
                <ul className="space-y-3 mb-6">
                  {premiumFeatures.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0" style={checkBadgeStyle}>
                        <Check className="w-4 h-4 text-[color:var(--color-text)]" />
                      </div>
                      <div className="text-sm text-[color:var(--color-text)]">{feature}</div>
                    </li>
                  ))}
                </ul>
              )}

              <button
                onClick={handleStartTrial}
                disabled={loading || isTrialActive || isPremium}
                className="w-full py-3 px-4 bg-[color:var(--color-warn)] text-[color:var(--color-text)] rounded-lg font-medium hover:brightness-95 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-[color:var(--color-text)] border-t-transparent rounded-full animate-spin"></div>
                    Starting Trial...
                  </>
                ) : isPremium ? 'You are Premium' : isTrialActive ? 'Trial Active' : shouldSubscribe ? 'Subscribe' : (
                  <>
                    <Zap className="w-4 h-4" />
                    Start Free Trial
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingModal;
